import {
    BookOpenIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    DatabaseIcon,
    EyeIcon,
    FileTextIcon,
    FolderOpenIcon,
    Loader2Icon,
    SparklesIcon,
    Trash2Icon,
    UploadIcon,
    XCircleIcon,
} from "lucide-react";
import {useCallback, useRef, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Button} from "@components/ui/button";
import {Card, CardContent} from "@components/ui/card";
import {Skeleton} from "@components/ui/skeleton";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@components/ui/table";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@components/ui/tooltip";
import {
    approveAndApplyAssumption,
    approveBusinessContextItem,
    createBusinessContextFromAssumption,
    deleteKnowledgeDoc,
    extractAssumptions,
    previewApplySuggestion,
    rejectAssumption,
    rejectBusinessContextItem,
    SuggestionPreviewData,
    uploadKnowledgeDoc,
    useVNextDocumentDetail,
    useVNextKnowledge,
    VNextAssumptionCandidate,
    VNextKnowledgeDocument,
} from "@hooks/use-vnext";

interface KnowledgeTabProps {
    workspaceId: string;
    scenarioId: string;
}

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".txt", ".md", ".csv", ".xlsx", ".html", ".htm"];
const MAX_SIZE_MB = 10;

function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusBadge({status}: {status: string}) {
    const variants: Record<string, {variant: "default" | "secondary" | "outline" | "destructive"; label: string}> = {
        proposed: {variant: "secondary", label: "Proposed"},
        approved: {variant: "default", label: "Approved"},
        rejected: {variant: "destructive", label: "Rejected"},
        committed: {variant: "outline", label: "Committed"},
        done: {variant: "default", label: "Done"},
        pending: {variant: "secondary", label: "Pending"},
        failed: {variant: "destructive", label: "Failed"},
        draft: {variant: "secondary", label: "Draft"},
        applied: {variant: "default", label: "Applied"},
    };
    const v = variants[status] ?? {variant: "secondary" as const, label: status};
    return <Badge variant={v.variant} className="text-xs">{v.label}</Badge>;
}

function ConfidenceBadge({confidence}: {confidence: number}) {
    const pct = Math.round(confidence * 100);
    const color = pct >= 80 ? "text-green-600" : pct >= 50 ? "text-amber-600" : "text-red-600";
    return <span className={`text-xs font-medium tabular-nums ${color}`}>{pct}%</span>;
}

function ContextTypeBadge({type}: {type: string}) {
    const labels: Record<string, string> = {
        event: "Event",
        decision: "Decision",
        risk: "Risk",
        one_off: "One-Off",
        constraint: "Constraint",
        operational_driver: "Op. Driver",
    };
    return <Badge variant="outline" className="text-xs">{labels[type] ?? type}</Badge>;
}

function filterValidFiles(files: FileList): {valid: File[]; skipped: string[]} {
    const valid: File[] = [];
    const skipped: string[] = [];
    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const ext = "." + (file.name.split(".").pop()?.toLowerCase() ?? "");
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            skipped.push(`${file.name} (unsupported type)`);
        } else if (file.size > MAX_SIZE_MB * 1024 * 1024) {
            skipped.push(`${file.name} (exceeds ${MAX_SIZE_MB} MB)`);
        } else {
            valid.push(file);
        }
    }
    return {valid, skipped};
}

interface UploadProgress {
    total: number;
    completed: number;
    current: string;
    failures: string[];
    skipped: string[];
}

const isForecastChange = (a: VNextAssumptionCandidate): boolean =>
    !!a.proposed_op_json?.target_node_id;

function formatNumber(value: number): string {
    if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
    if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
    return `$${value.toFixed(2)}`;
}

// ── Suggestion Preview Panel (local) ─────────────────────────

function SuggestionPreviewPanel({preview}: {preview: SuggestionPreviewData}) {
    if (preview.deltas.length === 0) {
        return <p className="text-sm text-muted-foreground py-2">No model changes predicted.</p>;
    }
    return (
        <div className="mt-2 rounded-md border p-3 bg-muted/30">
            <p className="text-xs font-medium mb-2">Preview: {preview.deltas.length} cell(s) affected</p>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="text-xs">Node</TableHead>
                        <TableHead className="text-xs">Month</TableHead>
                        <TableHead className="text-xs text-right">Before</TableHead>
                        <TableHead className="text-xs text-right">After</TableHead>
                        <TableHead className="text-xs text-right">Delta</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {preview.deltas.slice(0, 10).map((d, i) => (
                        <TableRow key={i}>
                            <TableCell className="text-xs">{d.node_label || d.cell}</TableCell>
                            <TableCell className="text-xs">{d.month_key}</TableCell>
                            <TableCell className="text-xs text-right">{formatNumber(parseFloat(d.before))}</TableCell>
                            <TableCell className="text-xs text-right">{formatNumber(parseFloat(d.after))}</TableCell>
                            <TableCell className="text-xs text-right font-medium">
                                {formatNumber(parseFloat(d.delta))}
                                {d.delta_pct ? ` (${d.delta_pct}%)` : ""}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {preview.deltas.length > 10 && (
                <p className="text-xs text-muted-foreground mt-1">
                    + {preview.deltas.length - 10} more changes
                </p>
            )}
        </div>
    );
}

// ── File Card (expanded) ──────────────────────────────────────

function FileCardExpanded({
    workspaceId,
    doc,
    onRefresh,
}: {
    workspaceId: string;
    doc: VNextKnowledgeDocument;
    onRefresh: () => void;
}) {
    const {document: detail, isLoading} = useVNextDocumentDetail(workspaceId, doc.id);
    const [actioningId, setActioningId] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<Record<string, SuggestionPreviewData>>({});
    const [reExtracting, setReExtracting] = useState(false);

    const handleReExtract = async () => {
        setReExtracting(true);
        setActionError(null);
        try {
            await extractAssumptions(workspaceId, doc.id);
            onRefresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Re-extraction failed");
        } finally {
            setReExtracting(false);
        }
    };

    if (isLoading || !detail) {
        return (
            <div className="space-y-2 px-4 py-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        );
    }

    const insights = detail.assumptions.filter((a) => !isForecastChange(a));
    const forecastChanges = detail.assumptions.filter(isForecastChange);

    const handleApproveAsContext = async (ac: VNextAssumptionCandidate) => {
        setActioningId(ac.id);
        setActionError(null);
        try {
            await createBusinessContextFromAssumption(workspaceId, ac.id);
            onRefresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed");
        } finally {
            setActioningId(null);
        }
    };

    const handleDismissInsight = async (ac: VNextAssumptionCandidate) => {
        setActioningId(ac.id);
        setActionError(null);
        try {
            await rejectAssumption(workspaceId, ac.id);
            onRefresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed");
        } finally {
            setActioningId(null);
        }
    };

    const handleApproveForForecast = async (ac: VNextAssumptionCandidate) => {
        setActioningId(ac.id);
        setActionError(null);
        try {
            await approveAndApplyAssumption(workspaceId, ac.id, ac.status);
            onRefresh();
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed");
        } finally {
            setActioningId(null);
        }
    };

    const handlePreviewImpact = async (suggestionId: string) => {
        if (previewData[suggestionId]) {
            setPreviewData((prev) => {
                const next = {...prev};
                delete next[suggestionId];
                return next;
            });
            return;
        }
        setActioningId(suggestionId);
        setActionError(null);
        try {
            const result = await previewApplySuggestion(workspaceId, suggestionId);
            setPreviewData((prev) => ({...prev, [suggestionId]: result}));
        } catch (err) {
            setActionError(err instanceof Error ? err.message : "Failed to load preview");
        } finally {
            setActioningId(null);
        }
    };

    return (
        <div className="space-y-4 px-4 py-3 bg-muted/20 border-t">
            {actionError && (
                <div className="border border-destructive/50 bg-destructive/5 rounded px-3 py-1.5 text-sm text-destructive">
                    {actionError}
                </div>
            )}

            {/* A. Document Preview + Re-extract */}
            {detail.extracted_text && (
                <div>
                    <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-medium text-muted-foreground">Document Preview</p>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={handleReExtract}
                            disabled={reExtracting}
                        >
                            {reExtracting ? (
                                <Loader2Icon className="size-3 mr-1 animate-spin" />
                            ) : (
                                <SparklesIcon className="size-3 mr-1" />
                            )}
                            Re-extract Insights
                        </Button>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        {detail.extracted_text.slice(0, 500)}
                        {detail.extracted_text.length > 500 ? "..." : ""}
                    </p>
                </div>
            )}

            {/* B. Extracted Insights */}
            {insights.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Extracted Insights ({insights.length})
                    </p>
                    <div className="space-y-2">
                        {insights.map((ac) => (
                            <div key={ac.id} className="rounded border p-2.5 bg-background">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">{ac.title}</p>
                                        {ac.rationale && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{ac.rationale}</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            <ConfidenceBadge confidence={ac.confidence} />
                                            <StatusBadge status={ac.status} />
                                        </div>
                                    </div>
                                    {ac.status === "proposed" && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-green-600 border-green-300"
                                                onClick={() => handleApproveAsContext(ac)}
                                                disabled={actioningId === ac.id}
                                            >
                                                {actioningId === ac.id ? (
                                                    <Loader2Icon className="size-3 animate-spin" />
                                                ) : (
                                                    <CheckCircleIcon className="size-3 mr-1" />
                                                )}
                                                Approve as Context
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-red-500"
                                                onClick={() => handleDismissInsight(ac)}
                                                disabled={actioningId === ac.id}
                                            >
                                                <XCircleIcon className="size-3 mr-1" />
                                                Dismiss
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                {ac.source_excerpt && (
                                    <p className="text-xs italic border-l-2 pl-2 mt-1.5 text-muted-foreground">
                                        &ldquo;{ac.source_excerpt.slice(0, 200)}&rdquo;
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* C. Extracted Metrics */}
            {(detail.datasets.length > 0 || detail.metrics.length > 0) && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Extracted Metrics ({detail.metrics.length})
                    </p>
                    {detail.metrics.length > 0 && (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="text-xs">Label</TableHead>
                                    <TableHead className="text-xs text-right">Value</TableHead>
                                    <TableHead className="text-xs">Month</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {detail.metrics.slice(0, 20).map((m) => (
                                    <TableRow key={m.id}>
                                        <TableCell className="text-xs">{m.label}</TableCell>
                                        <TableCell className="text-xs text-right tabular-nums">
                                            {m.meta?.unit === "percentage"
                                                ? `${m.value.toFixed(1)}%`
                                                : formatNumber(m.value)}
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {m.month_key || "\u2014"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            )}

            {/* D. Suggested Forecast Changes */}
            {(forecastChanges.length > 0 || detail.suggestions.length > 0) && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Suggested Forecast Changes ({forecastChanges.length + detail.suggestions.length})
                    </p>
                    <div className="space-y-2">
                        {forecastChanges.map((ac) => (
                            <div key={ac.id} className="rounded border p-2.5 bg-background">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">{ac.title}</p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <ConfidenceBadge confidence={ac.confidence} />
                                            <StatusBadge status={ac.status} />
                                        </div>
                                    </div>
                                    {(ac.status === "proposed" || ac.status === "approved") && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-green-600 border-green-300"
                                                onClick={() => handleApproveForForecast(ac)}
                                                disabled={actioningId === ac.id}
                                            >
                                                {actioningId === ac.id ? (
                                                    <Loader2Icon className="size-3 animate-spin" />
                                                ) : (
                                                    <CheckCircleIcon className="size-3 mr-1" />
                                                )}
                                                {ac.status === "approved" ? "Apply" : "Approve for Forecast"}
                                            </Button>
                                            {ac.status === "proposed" && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 px-2 text-xs text-red-500"
                                                    onClick={() => handleDismissInsight(ac)}
                                                    disabled={actioningId === ac.id}
                                                >
                                                    <XCircleIcon className="size-3 mr-1" />
                                                    Dismiss
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {detail.suggestions.map((s) => (
                            <div key={s.id} className="rounded border p-2.5 bg-background">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium">
                                            {s.target_label || s.suggestion_type}
                                        </p>
                                        {s.rationale && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{s.rationale}</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            <ConfidenceBadge confidence={s.confidence} />
                                            <StatusBadge status={s.status} />
                                        </div>
                                    </div>
                                    {s.status !== "applied" && s.status !== "rejected" && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs"
                                                            onClick={() => handlePreviewImpact(s.id)}
                                                            disabled={actioningId === s.id}
                                                        >
                                                            <EyeIcon className="size-3 mr-1" />
                                                            Preview Impact
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Preview impact on model</TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    )}
                                </div>
                                {previewData[s.id] && <SuggestionPreviewPanel preview={previewData[s.id]} />}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* E. Context Items Panel */}
            {detail.context_items.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                        Business Context ({detail.context_items.length})
                    </p>
                    <div className="space-y-2">
                        {detail.context_items.map((ci) => (
                            <div key={ci.id} className="rounded border p-2.5 bg-background">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <ContextTypeBadge type={ci.context_type} />
                                            <span className="text-sm font-medium truncate">{ci.title}</span>
                                        </div>
                                        {ci.summary && (
                                            <p className="text-xs text-muted-foreground mt-0.5">{ci.summary}</p>
                                        )}
                                        {ci.source_excerpt && (
                                            <p className="text-xs italic border-l-2 pl-2 mt-1.5 text-muted-foreground">
                                                &ldquo;{ci.source_excerpt.slice(0, 200)}&rdquo;
                                            </p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            {ci.impact_hint && (
                                                <Badge variant="outline" className="text-[10px]">
                                                    {ci.impact_hint} {ci.direction === "up" ? "↑" : ci.direction === "down" ? "↓" : "↔"}
                                                </Badge>
                                            )}
                                            <StatusBadge status={ci.status} />
                                        </div>
                                    </div>
                                    {ci.status === "draft" && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-green-600 border-green-300"
                                                onClick={async () => {
                                                    setActioningId(ci.id);
                                                    setActionError(null);
                                                    try {
                                                        await approveBusinessContextItem(workspaceId, ci.id);
                                                        onRefresh();
                                                    } catch (err) {
                                                        setActionError(err instanceof Error ? err.message : "Failed");
                                                    } finally {
                                                        setActioningId(null);
                                                    }
                                                }}
                                                disabled={actioningId === ci.id}
                                            >
                                                {actioningId === ci.id ? (
                                                    <Loader2Icon className="size-3 animate-spin" />
                                                ) : (
                                                    <CheckCircleIcon className="size-3 mr-1" />
                                                )}
                                                Approve
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-xs text-red-500"
                                                onClick={async () => {
                                                    setActioningId(ci.id);
                                                    setActionError(null);
                                                    try {
                                                        await rejectBusinessContextItem(workspaceId, ci.id);
                                                        onRefresh();
                                                    } catch (err) {
                                                        setActionError(err instanceof Error ? err.message : "Failed");
                                                    } finally {
                                                        setActioningId(null);
                                                    }
                                                }}
                                                disabled={actioningId === ci.id}
                                            >
                                                <XCircleIcon className="size-3 mr-1" />
                                                Dismiss
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── File Card ─────────────────────────────────────────────────

function FileCard({
    workspaceId,
    doc,
    isExpanded,
    onToggle,
    onDelete,
    isDeleting,
    onRefresh,
}: {
    workspaceId: string;
    doc: VNextKnowledgeDocument;
    isExpanded: boolean;
    onToggle: () => void;
    onDelete: () => void;
    isDeleting: boolean;
    onRefresh: () => void;
}) {
    const _mimeMap: Record<string, string> = {
        "pdf": "PDF", "csv": "CSV", "plain": "TXT", "html": "HTML", "json": "JSON",
        "spreadsheetml.sheet": "Excel", "wordprocessingml.document": "Word",
        "presentationml.presentation": "PPT", "msword": "Word", "ms-excel": "Excel",
    };
    const rawType = doc.mime_type.split("/").pop() ?? "";
    const typeBadge = _mimeMap[rawType] ?? (rawType.length > 10 ? rawType.split(".").pop() ?? rawType : rawType);
    const contextCount = doc.context_count ?? 0;
    const datasetCount = doc.dataset_count ?? 0;
    const suggestionCount = doc.suggestion_count ?? 0;
    const assumptionCount = doc.assumption_count ?? 0;

    return (
        <Card className="overflow-hidden group/card">
            <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={onToggle}
            >
                <div className="shrink-0">
                    {isExpanded
                        ? <ChevronDownIcon className="size-4 text-muted-foreground" />
                        : <ChevronRightIcon className="size-4 text-muted-foreground" />}
                </div>
                <FileTextIcon className="size-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{doc.filename}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-xs">{typeBadge}</Badge>
                        <span className="text-xs text-muted-foreground tabular-nums">{formatBytes(doc.size_bytes)}</span>
                        <StatusBadge status={doc.extraction_status} />
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {assumptionCount > 0 && (
                        <Badge variant="secondary" className="text-xs tabular-nums">
                            <SparklesIcon className="size-3 mr-1" />{assumptionCount}
                        </Badge>
                    )}
                    {datasetCount > 0 && (
                        <Badge variant="secondary" className="text-xs tabular-nums">
                            <DatabaseIcon className="size-3 mr-1" />{datasetCount}
                        </Badge>
                    )}
                    {contextCount > 0 && (
                        <Badge variant="secondary" className="text-xs tabular-nums">
                            <BookOpenIcon className="size-3 mr-1" />{contextCount}
                        </Badge>
                    )}
                    {suggestionCount > 0 && (
                        <Badge variant="secondary" className="text-xs tabular-nums">
                            {suggestionCount} suggestion{suggestionCount !== 1 ? "s" : ""}
                        </Badge>
                    )}
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0 text-muted-foreground/40 opacity-0 group-hover/card:opacity-100 hover:text-red-500 transition-opacity"
                                    onClick={onDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <Loader2Icon className="size-3.5 animate-spin" />
                                    ) : (
                                        <Trash2Icon className="size-3.5" />
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete Document</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
            {isExpanded && (
                <FileCardExpanded
                    workspaceId={workspaceId}
                    doc={doc}
                    onRefresh={onRefresh}
                />
            )}
        </Card>
    );
}

// ── Main KnowledgeTab ─────────────────────────────────────────

export function KnowledgeTab({workspaceId}: KnowledgeTabProps) {
    const {documents, isLoading: docsLoading, refresh: refreshDocs} = useVNextKnowledge(workspaceId);
    const [expandedDocId, setExpandedDocId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    const handleRefresh = useCallback(() => {
        refreshDocs();
    }, [refreshDocs]);

    const processFiles = useCallback(async (files: FileList) => {
        const {valid, skipped} = filterValidFiles(files);
        if (valid.length === 0) {
            if (skipped.length > 0) {
                setUploadError(`No valid files found. Skipped: ${skipped.join(", ")}`);
            }
            return;
        }

        setUploadError(null);
        setUploadProgress({total: valid.length, completed: 0, current: valid[0].name, failures: [], skipped});

        const failures: string[] = [];
        for (let i = 0; i < valid.length; i++) {
            const file = valid[i];
            setUploadProgress((prev) => prev ? {...prev, current: file.name, completed: i} : prev);
            try {
                await uploadKnowledgeDoc(workspaceId, file);
            } catch (err) {
                const msg = err instanceof Error ? err.message : "Upload failed";
                failures.push(`${file.name}: ${msg}`);
            }
        }

        setUploadProgress(null);
        handleRefresh();

        if (failures.length > 0) {
            setUploadError(`${valid.length - failures.length}/${valid.length} uploaded. Failed: ${failures.join("; ")}`);
        } else if (skipped.length > 0) {
            setUploadError(`${valid.length} uploaded. Skipped ${skipped.length} file(s): ${skipped.join(", ")}`);
        }
    }, [workspaceId, handleRefresh]);

    const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        await processFiles(files);
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [processFiles]);

    const handleFolderUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        await processFiles(files);
        if (folderInputRef.current) folderInputRef.current.value = "";
    }, [processFiles]);

    const handleDelete = useCallback(async (docId: string) => {
        setDeletingId(docId);
        try {
            await deleteKnowledgeDoc(workspaceId, docId);
            if (expandedDocId === docId) setExpandedDocId(null);
            handleRefresh();
        } catch {
            // Swallow
        } finally {
            setDeletingId(null);
        }
    }, [workspaceId, handleRefresh, expandedDocId]);

    const docCount = documents.length;

    return (
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <BookOpenIcon className="size-5 text-[#209DB3]" />
                    <h2 className="text-lg font-semibold">Knowledge</h2>
                    <span className="text-sm text-muted-foreground">
                        {docCount} file{docCount !== 1 ? "s" : ""}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {/* Hidden file inputs */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ALLOWED_EXTENSIONS.join(",")}
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                    />
                    <input
                        ref={folderInputRef}
                        type="file"
                        /* @ts-expect-error webkitdirectory is not in the TS types */
                        webkitdirectory=""
                        onChange={handleFolderUpload}
                        className="hidden"
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => folderInputRef.current?.click()}
                    >
                        <FolderOpenIcon className="mr-1.5 size-3.5" />
                        Upload Folder
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <UploadIcon className="mr-1.5 size-3.5" />
                        Upload Files
                    </Button>
                </div>
            </div>

            {/* Upload progress */}
            {uploadProgress && (
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2Icon className="size-4 animate-spin shrink-0" />
                        <span className="truncate">
                            Uploading {uploadProgress.completed + 1} of {uploadProgress.total}: {uploadProgress.current}
                        </span>
                    </div>
                    <div className="w-full bg-zinc-100 rounded-full h-1.5">
                        <div
                            className="bg-[#209DB3] h-1.5 rounded-full transition-all duration-300"
                            style={{width: `${((uploadProgress.completed) / uploadProgress.total) * 100}%`}}
                        />
                    </div>
                </div>
            )}
            {uploadError && (
                <p className="text-sm text-amber-600">{uploadError}</p>
            )}

            {/* File Cards */}
            {docsLoading ? (
                <div className="space-y-2">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
            ) : documents.length === 0 ? (
                <Card>
                    <CardContent className="py-8">
                        <div className="text-center text-muted-foreground">
                            <FileTextIcon className="size-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">No documents uploaded yet</p>
                            <p className="text-xs mt-1">
                                Upload PDF, DOCX, TXT, MD, CSV, or XLSX files (max 10 MB each)
                            </p>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {documents.map((doc) => (
                        <FileCard
                            key={doc.id}
                            workspaceId={workspaceId}
                            doc={doc}
                            isExpanded={expandedDocId === doc.id}
                            onToggle={() => setExpandedDocId(expandedDocId === doc.id ? null : doc.id)}
                            onDelete={() => handleDelete(doc.id)}
                            isDeleting={deletingId === doc.id}
                            onRefresh={handleRefresh}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
